import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, MapPin, Briefcase, Inbox as InboxIcon, FileText, UserPlus, Target } from "lucide-react";

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Inbox = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.role === "organisation") {
      api.get("/inbox").then((r) => setItems(r.data)).finally(() => setLoading(false));
    } else if (user) {
      setLoading(false);
    }
  }, [user]);

  if (authLoading || (user?.role === "organisation" && loading) || (!user))
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  if (user.role !== "organisation") {
    return (
      <PageShell>
        <div className="mx-auto max-w-lg px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-[#0B2545]">Organisations only</h1>
          <p className="mt-2 text-slate-600">The inbox shows professionals who applied to your roles or connected with you.</p>
          <Button onClick={() => navigate("/opportunities")} className="mt-6 h-11 rounded-xl bg-[#16A34A] px-6 font-semibold text-white hover:bg-[#138a3f]">Browse Opportunities</Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-[1000px] px-6 py-10 lg:px-8">
        <div className="flex items-center gap-2">
          <InboxIcon className="h-6 w-6 text-[#1E4FA3]" />
          <h1 className="font-heading text-[34px] font-extrabold text-[#0B2545] sm:text-[40px]">Applicant Inbox</h1>
        </div>
        <p className="mt-2 text-[16px] text-slate-600">Professionals who applied to your roles or that you've connected with.</p>

        {items.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
            <p className="text-slate-500">No activity yet.</p>
            <p className="mt-1 text-sm text-slate-400">Post a requirement and connect with professionals to see them here.</p>
            <div className="mt-5 flex justify-center gap-3">
              <Button onClick={() => navigate("/post-requirement")} className="h-11 rounded-xl bg-[#1E4FA3] px-5 font-semibold text-white hover:bg-[#193f83]">Post a Requirement</Button>
              <Button onClick={() => navigate("/professionals")} variant="outline" className="h-11 rounded-xl border-2 border-slate-200 px-5 font-semibold text-slate-700 hover:bg-slate-50">Browse Professionals</Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {items.map((it) => {
              const pro = it.professional || {};
              const prefs = pro.preferences || {};
              const isApp = it.kind === "application";
              return (
                <div key={it.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#16A34A]/10 font-heading text-[15px] font-bold text-[#16A34A]">{initials(it.professional_name)}</div>
                      <div>
                        <h3 className="font-heading text-[17px] font-bold leading-tight text-[#0B2545]">{it.professional_name}</h3>
                        {pro.title && <p className="text-sm text-slate-600">{pro.title}</p>}
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                          {pro.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{pro.location}</span>}
                          {typeof pro.experience_years === "number" && <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{pro.experience_years} yrs</span>}
                        </div>
                      </div>
                    </div>
                    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${isApp ? "bg-[#1E4FA3]/10 text-[#1E4FA3]" : "bg-[#16A34A]/10 text-[#16A34A]"}`}>
                      {isApp ? <FileText className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                      {isApp ? `Applied${it.job_title ? ": " + it.job_title : ""}` : "Connected"}
                    </span>
                  </div>

                  {/* Preferences */}
                  {(prefs.desired_role || (prefs.preferred_locations || []).length > 0 || (prefs.employment_types || []).length > 0) && (
                    <div className="mt-4 rounded-xl bg-slate-50 p-4">
                      {prefs.desired_role && (
                        <p className="flex items-center gap-1.5 text-sm text-slate-700"><Target className="h-4 w-4 text-[#16A34A]" /><span className="font-semibold">Seeking:</span> {prefs.desired_role}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(prefs.employment_types || []).map((t) => <span key={t} className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">{t}</span>)}
                        {(prefs.preferred_locations || []).map((l) => <span key={l} className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">{l}</span>)}
                      </div>
                    </div>
                  )}

                  <p className="mt-3 text-xs text-slate-400">{new Date(it.created_at).toLocaleString()}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Inbox;
